/**
 * PropertyPhoto (docs/04 §3 "Property photo card (S3 image w/ icon fallback)").
 *
 * Renders a property's photo at one of the stored sizes, or the documented
 * fallback treatment — the photo gradient plus a building icon — when there is
 * no photo. The fallback is deliberately a real design, not an empty box: most
 * properties will have no photo for a while, and a list of grey rectangles reads
 * as broken rather than as "not added yet".
 *
 * Every size is 16:9 because that is what the server crops to (ADR-26), so the
 * frame never letterboxes or crops a second time in CSS.
 */

import { clsx } from 'clsx';
import { Building2 } from 'lucide-react';
import type { ReactNode } from 'react';

/** Matches the derivatives the server stores (services/images/property-photo). */
export type PhotoSize = 'card' | 'thumb';

export interface PropertyPhotoProps {
  readonly propertyId: string;
  readonly photoKey: string | null;
  /** NULL for a pre-4.3 photo, which resolves through the legacy layout. */
  readonly photoDerivedKey: string | null;
  /** Used for the alt text, so it says which property rather than "photo". */
  readonly name: string;
  readonly size: PhotoSize;
  readonly className?: string | undefined;
}

/**
 * The image URL. BOTH keys are in the path, which is what lets the server mark
 * it immutable: a replaced photo has a different `photoKey`, and a re-cropped
 * one has a different `derivedKey`, so neither can ever mean different bytes
 * under a URL a browser was told to cache for a year (punch 4.3).
 *
 * `derivedKey === null` is a PRE-4.3 photo, served from the legacy layout. Its
 * objects were never rewritten, so the old URL is still the right one for it.
 */
export function propertyPhotoUrl(
  propertyId: string,
  photoKey: string,
  derivedKey: string | null,
  size: PhotoSize,
): string {
  const base = `/api/v1/properties/${propertyId}/photo/${photoKey}`;
  return derivedKey === null ? `${base}/${size}` : `${base}/d/${derivedKey}/${size}`;
}

/**
 * The SOURCE URL — the uncropped image the cropper reopens. Editor-only and
 * never cached, server-side (docs/11 F-57); this is only where the address is
 * written down.
 */
export function propertyPhotoSourceUrl(propertyId: string, photoKey: string): string {
  return `/api/v1/properties/${propertyId}/photo/${photoKey}/source`;
}

export function PropertyPhoto({
  propertyId,
  photoKey,
  photoDerivedKey,
  name,
  size,
  className,
}: PropertyPhotoProps): ReactNode {
  const frame = clsx('relative aspect-video overflow-hidden bg-photo-grad-a', className);

  if (photoKey === null) {
    return (
      <div
        // The fallback carries no alt text and is not announced: "no photo" is
        // not information a screen-reader user needs read out on every row.
        aria-hidden
        className={clsx(
          frame,
          'flex items-center justify-center bg-linear-to-br from-photo-grad-a to-photo-grad-b',
        )}
      >
        <Building2 className="size-1/3 max-h-10 text-ink3" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <img
      src={propertyPhotoUrl(propertyId, photoKey, photoDerivedKey, size)}
      alt={`${name} building photo`}
      // Dimensions are known and fixed, so the browser reserves the space before
      // the bytes arrive — no layout shift as a list of photos loads.
      width={size === 'card' ? 1024 : 160}
      height={size === 'card' ? 576 : 90}
      loading="lazy"
      decoding="async"
      className={clsx(frame, 'object-cover')}
    />
  );
}
